// cadProjectAdapter.ts — Transform cv-cad project-named-export → cv-web CVProject
//
// cv-cad cvxpproj.lsp exports project-level data with:
//   - titleBlock (raw ATTDEF tags + semantic-named fields)
//   - xrecordHeaders (panel/site dictionary metadata)
//   - runtimeGlobals (program state, mostly null in exports)
//   - meta (host, drawingName, drawingPath)
//
// This adapter maps the titleBlock named fields and xrecord headers
// into a cv-web CVProject with default panels and settings.

import type { CVProject, ProjectSettings } from '../types/panelvar';
import { defaultProjectSettings, defaultElevations } from '../types/panelvar';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CadProjectExport {
  cvVersion:  string;
  type:       string;
  exportedAt: string;
  buildId?:   string;
  meta: {
    host:        string;
    drawingName: string;
    drawingPath: string;
  };
  xrecordHeaders: {
    panel: {
      found:       boolean;
      version:     string | null;
      projectName: string | null;
      panelPrefix: string | null;
      dateCode:    string | null;
    };
    site: {
      found:       boolean;
      version:     string | null;
      projectName: string | null;
    };
    consistent: boolean;
  };
  titleBlock: {
    raw:   Record<string, string>;
    named: Record<string, string>;
  } | null;
  runtimeGlobals: Record<string, unknown>;
  diagnostics?: Record<string, unknown>;
}

export interface CadProjectImportResult {
  project:  CVProject;
  warnings: string[];
}

// ── Detection ──────────────────────────────────────────────────────────────────

export function isCadProjectExport(obj: Record<string, unknown>): boolean {
  return obj['type'] === 'project-named-export'
    && typeof obj['meta'] === 'object'
    && obj['meta'] !== null;
}

// ── Conversion ─────────────────────────────────────────────────────────────────

export function importCadProject(raw: CadProjectExport): CadProjectImportResult {
  const warnings: string[] = [];
  const now = new Date().toISOString();

  const named = raw.titleBlock?.named ?? {};
  const xrec  = raw.xrecordHeaders;

  // ── Project name: prefer xrecord, fallback to titleBlock
  const projectName = xrec.panel.projectName?.trim()
    || named['projectName']?.trim()
    || raw.meta.drawingName?.replace(/\.dwg$/i, '')
    || 'Imported Project';

  // ── Client/engineer from titleBlock
  const client   = named['contractor']?.trim() || '';
  const engineer = named['drawnBy']?.trim() || '';

  // ── Address from titleBlock
  const addr1 = named['addressLine1']?.trim() || '';
  const addr2 = named['addressLine2']?.trim() || '';
  const address = [addr1, addr2].filter(Boolean).join(', ');

  // ── Job number from drawingNumber or drawing file
  const jobNumber = named['drawingNumber']?.trim()
    || raw.meta.drawingName?.replace(/\.dwg$/i, '')
    || '';

  // ── Settings
  const settings: ProjectSettings = defaultProjectSettings();

  // NOTE: titleBlock named fields 'concreteStrength' (D1) and 'concreteUnitWeight' (D2)
  // are actually panel dimension strings (e.g. "44'-10 3/16\" D.F."), NOT PSI/PCF values.
  // Real concrete values live in runtimeGlobals.structuralSettings, which is null for
  // file-based exports. We use defaults and let the user correct in Project Settings.
  const rtGlobals = raw.runtimeGlobals as Record<string, unknown> | undefined;
  const structSettings = rtGlobals?.['structuralSettings'] as Record<string, unknown> | undefined;
  if (structSettings) {
    const psi = structSettings['concreteStrengthPSI'];
    if (typeof psi === 'number' && psi > 0) settings.concreteStrength = psi;
    const pcf = structSettings['concreteDensityPCF'];
    if (typeof pcf === 'number' && pcf > 0) settings.concreteDensity = pcf;
  }

  // ── Metadata warnings
  if (!raw.titleBlock) {
    warnings.push('No title block found in drawing — project metadata may be incomplete.');
  }

  if (!xrec.consistent) {
    warnings.push('Panel and site xrecord headers are inconsistent — project may combine data from different sources.');
  }

  if (raw.diagnostics) {
    const nilCount = raw.diagnostics['nilCount'];
    const totalCount = raw.diagnostics['globalCount'];
    if (typeof nilCount === 'number' && typeof totalCount === 'number' && nilCount > totalCount * 0.8) {
      warnings.push(`${nilCount} of ${totalCount} runtime globals are nil — this is normal for file-based exports.`);
    }
  }

  const project: CVProject = {
    id:        crypto.randomUUID(),
    name:      projectName,
    buildingName: '',
    jobNumber,
    client,
    engineer,
    superintendent: '',
    address:   address || undefined,
    city:      '',
    state:     '',
    phone:     '',
    fax:       '',
    email:     '',
    panels:    [],    // Panels must be imported separately via panel-named-export
    site:      null,  // Site must be imported separately via site-named-export
    plans:     null,
    elevations: defaultElevations(),
    settings,
    createdAt: now,
    updatedAt: now,
  };

  warnings.push(
    'Project imported from CV-CAD title block — panels and site data must be imported separately.'
  );

  return { project, warnings };
}
